import type { BandwidthQuota } from "./types/api";

export type QuotaLevel = "healthy" | "warning" | "critical";

export interface QuotaCycle {
  used: number;
  quota: number;
  remaining: number;
  percent: number;
  /** Share of the billing cycle that has elapsed, 0-100. */
  elapsedPercent: number;
  daysLeft: number;
  /** Usage at reset if the current cycle's average pace continues; null too early to tell. */
  projected: number | null;
  level: QuotaLevel;
}

const DAY = 86_400_000;

/** KiwiVM resets monthly, so the cycle started one calendar month before the next reset. */
function cycleStart(resetMs: number): number {
  const start = new Date(resetMs);
  start.setUTCMonth(start.getUTCMonth() - 1);
  return start.getTime();
}

export function quotaCycle(quota: BandwidthQuota, nowMs = Date.now()): QuotaCycle | null {
  const { used_bytes: used, quota_bytes: total, reset_at: resetAt } = quota;
  if (used == null || total == null || resetAt == null || total <= 0) return null;
  const resetMs = resetAt * 1000;
  const startMs = cycleStart(resetMs);
  const elapsed = Math.min(1, Math.max(0, (nowMs - startMs) / (resetMs - startMs)));
  const percent = (used / total) * 100;
  // Under a day of data says little about the month; skip the forecast until then.
  const projected = nowMs - startMs >= DAY && elapsed > 0 ? used / elapsed : null;
  const level: QuotaLevel =
    percent >= 90 || (projected != null && projected >= total)
      ? "critical"
      : percent >= 75 || (projected != null && projected >= total * 0.9)
        ? "warning"
        : "healthy";
  return {
    used,
    quota: total,
    remaining: Math.max(0, total - used),
    percent,
    elapsedPercent: elapsed * 100,
    daysLeft: Math.max(0, (resetMs - nowMs) / DAY),
    projected,
    level
  };
}

/** KiwiVM's panel labels binary gigabytes (2^30 bytes) as "GB"; match it so numbers line up. */
export function formatPanelGb(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "N/A";
  return `${(value / 2 ** 30).toFixed(2)} GB`;
}
